/**
 * Plain-language weather for a camera crew that is not aviation-trained (pure; no I/O).
 *
 * Turns the latest METAR (or, when there is none, the general forecast-grid picture) into words a
 * producer reads in one glance: "Partly cloudy", "Low clouds at ~800 ft", "Can see 10+ miles",
 * "Wind from the west 10 mph, gusts 20 mph", "64°F", "Rain chance next 3 h: up to 40%", plus short
 * "for filming" light notes and a one-line chip summary ("☀️ Sunny · W 10 mph").
 *
 * Weather is reported as published / forecast — never as a go/no-go. Text deliberately avoids
 * "safe" / "cleared" (a judgement this app never makes). Flight categories are shown as published, with
 * categoryMeaning() beside them.
 */
import type { EpochMs, FlightCategory, GeneralWeather, GeneralWeatherHour, MetarReport } from './types.ts';
import { angleDiffDeg, normalizeDeg } from './geo.ts';
import { formatInt } from './format.ts';

export const MPH_PER_KT = 1.150779;
export const MI_PER_M = 1 / 1609.344;

export const ktToMph = (kt: number) => kt * MPH_PER_KT;
export const cToF = (c: number) => (c * 9) / 5 + 32;

const ok = (n: number | null | undefined): n is number => n != null && Number.isFinite(n);

// ───────────────────────────── Sky ─────────────────────────────

export type SkyKind =
  | 'sunny'
  | 'mostly_sunny'
  | 'partly_cloudy'
  | 'mostly_cloudy'
  | 'overcast'
  | 'fog'
  | 'haze'
  | 'drizzle'
  | 'light_rain'
  | 'rain'
  | 'heavy_rain'
  | 'showers'
  | 'snow'
  | 'thunder'
  | 'unknown';

export interface Sky {
  kind: SkyKind;
  /** Emoji icon (night-aware for the fair-weather kinds). */
  icon: string;
  /** "Sunny" / "No clouds" (night) / "Partly cloudy" / "Fog" / "Light rain" / "Haze/smoke" … */
  text: string;
}

const SKY_TEXT: Record<SkyKind, string> = {
  sunny: 'Sunny',
  mostly_sunny: 'Mostly sunny',
  partly_cloudy: 'Partly cloudy',
  mostly_cloudy: 'Mostly cloudy',
  overcast: 'Overcast',
  fog: 'Fog',
  haze: 'Haze/smoke',
  drizzle: 'Drizzle',
  light_rain: 'Light rain',
  rain: 'Rain',
  heavy_rain: 'Heavy rain',
  showers: 'Showers',
  snow: 'Snow',
  thunder: 'Thunderstorms',
  unknown: 'Sky not reported',
};

const SKY_ICON: Record<SkyKind, string> = {
  sunny: '☀️',
  mostly_sunny: '🌤️',
  partly_cloudy: '⛅',
  mostly_cloudy: '🌥️',
  overcast: '☁️',
  fog: '🌫️',
  haze: '🌫️',
  drizzle: '🌦️',
  light_rain: '🌦️',
  rain: '🌧️',
  heavy_rain: '🌧️',
  showers: '🌦️',
  snow: '❄️',
  thunder: '⛈️',
  unknown: '·',
};

export function skyOf(kind: SkyKind, isDay = true): Sky {
  if (!isDay && (kind === 'sunny' || kind === 'mostly_sunny')) {
    return { kind, icon: '🌙', text: kind === 'sunny' ? 'Clear sky' : 'A few clouds' };
  }
  if (!isDay && kind === 'partly_cloudy') return { kind, icon: '☁️', text: SKY_TEXT[kind] };
  return { kind, icon: SKY_ICON[kind], text: SKY_TEXT[kind] };
}

/** Sky kind from METAR present weather + cloud layers. */
export function skyFromMetar(m: Pick<MetarReport, 'wx' | 'clouds' | 'visibilitySm'>): SkyKind {
  const wx = (m.wx ?? '').toUpperCase();
  if (/TS/.test(wx)) return 'thunder';
  if (/SN|SG|PL|GR|GS/.test(wx)) return 'snow';
  if (/SHRA|SH\b/.test(wx)) return 'showers';
  if (/(^|\s)\+[A-Z]*RA/.test(wx)) return 'heavy_rain';
  if (/(^|\s)-[A-Z]*RA/.test(wx)) return 'light_rain';
  if (/RA/.test(wx)) return 'rain';
  if (/DZ/.test(wx)) return 'drizzle';
  if (/FG/.test(wx)) return 'fog';
  if (/HZ|FU|DU|SA|VA/.test(wx)) return 'haze';
  const covers = m.clouds.map((c) => c.cover);
  if (covers.includes('VV')) return ok(m.visibilitySm) && m.visibilitySm < 1 ? 'fog' : 'overcast';
  if (/BR/.test(wx) && ok(m.visibilitySm) && m.visibilitySm < 1) return 'fog';
  if (covers.includes('OVC')) return 'overcast';
  if (covers.includes('BKN')) return 'mostly_cloudy';
  if (covers.includes('SCT')) return 'partly_cloudy';
  if (covers.includes('FEW')) return 'mostly_sunny';
  if (/BR/.test(wx)) return 'haze';
  return 'sunny';
}

/** Sky kind from a WMO weather code (WMO), falling back to cloud cover %. */
export function skyFromWmo(code: number | null, cloudCoverPct: number | null): SkyKind {
  if (ok(code)) {
    if (code >= 95) return 'thunder';
    if (code >= 85) return 'snow';
    if (code >= 80) return code === 82 ? 'heavy_rain' : 'showers';
    if (code >= 71) return 'snow';
    if (code >= 61) return code === 61 || code === 66 ? 'light_rain' : code === 65 || code === 67 ? 'heavy_rain' : 'rain';
    if (code >= 51) return 'drizzle';
    if (code === 45 || code === 48) return 'fog';
    if (code === 3) return 'overcast';
    if (code === 2) return 'partly_cloudy';
    if (code === 1) return 'mostly_sunny';
    if (code === 0) return 'sunny';
  }
  if (!ok(cloudCoverPct)) return 'unknown';
  if (cloudCoverPct < 15) return 'sunny';
  if (cloudCoverPct < 40) return 'mostly_sunny';
  if (cloudCoverPct < 70) return 'partly_cloudy';
  if (cloudCoverPct < 90) return 'mostly_cloudy';
  return 'overcast';
}

const isWet = (k: SkyKind) => k === 'drizzle' || k === 'light_rain' || k === 'rain' || k === 'heavy_rain' || k === 'showers' || k === 'snow' || k === 'thunder';

// ───────────────────────────── Words ─────────────────────────────

const DIR8 = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
const DIR8_SHORT = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** "west", "southwest" (8-point, lower case). */
export function directionWord(deg: number): string {
  return DIR8[Math.round(normalizeDeg(deg) / 45) % 8]!;
}

/** "W", "SW" (8-point). */
export function directionShort(deg: number): string {
  return DIR8_SHORT[Math.round(normalizeDeg(deg) / 45) % 8]!;
}

/** Feet rounded for a crew: nearest 100 below 3,000 ft, else nearest 500. "~1,200 ft". */
export function roughFeet(ft: number): string {
  const r = ft < 3000 ? Math.round(ft / 100) * 100 : Math.round(ft / 500) * 500;
  return `~${formatInt(Math.max(100, r))} ft`;
}

function miles(mi: number): string {
  if (mi < 1) return mi < 0.375 ? '¼ mile' : mi < 0.625 ? '½ mile' : '¾ mile';
  const r = Math.round(mi);
  return `${r} mile${r === 1 ? '' : 's'}`;
}

/** What a published flight category means, in plain words (shown beside the VFR / MVFR / IFR / LIFR chip). */
export function categoryMeaning(cat: FlightCategory | null | undefined): string | null {
  if (cat === 'VFR') return 'Good visibility — clouds above 3,000 ft or none, can see 5+ miles';
  if (cat === 'MVFR') return 'Marginal — clouds 1,000–3,000 ft or visibility 3–5 miles';
  if (cat === 'IFR') return 'Low clouds or poor visibility — clouds 500–1,000 ft or 1–3 miles';
  if (cat === 'LIFR') return 'Very low clouds or fog — clouds under 500 ft or under 1 mile';
  return null;
}

/** Plain words for a flight category; VFR (and unknown) say nothing. */
export function categoryPlain(cat: FlightCategory | null | undefined): string | null {
  if (cat === 'MVFR') return 'Low clouds / haze';
  if (cat === 'IFR' || cat === 'LIFR') return 'Very low clouds / fog';
  return null;
}

/** Cloud height line: "Very low clouds at ~800 ft", "Low clouds at ~2,500 ft", "Clouds at ~6,000 ft". */
export function cloudText(m: Pick<MetarReport, 'ceilingFt' | 'clouds'>): string | null {
  if (ok(m.ceilingFt)) {
    if (m.ceilingFt < 1000) return `Very low clouds at ${roughFeet(m.ceilingFt)}`;
    if (m.ceilingFt < 3000) return `Low clouds at ${roughFeet(m.ceilingFt)}`;
    return `Cloud layer at ${roughFeet(m.ceilingFt)}`;
  }
  const scattered = m.clouds.filter((c) => (c.cover === 'FEW' || c.cover === 'SCT') && ok(c.baseFt)).sort((a, b) => a.baseFt! - b.baseFt!)[0];
  if (scattered) return `Some clouds at ${roughFeet(scattered.baseFt!)}`;
  return null;
}

/** Visibility line from statute miles (+ METAR present weather for the reason). */
export function visibilityPlain(mi: number | null, wx: string | null = null): string | null {
  if (!ok(mi)) return null;
  const w = (wx ?? '').toUpperCase();
  const why = /FG/.test(w) ? 'Foggy' : /FU/.test(w) ? 'Smoky' : /BR/.test(w) ? 'Misty' : /HZ|DU|SA/.test(w) ? 'Hazy' : /RA|DZ|SN/.test(w) ? 'Rain' : null;
  if (mi >= 9.5) return 'Can see 10+ miles';
  if (mi >= 6) return `Can see ~${Math.round(mi)} miles`;
  if (mi >= 3) return `${why ?? 'Hazy'} — about ${miles(mi)}`;
  if (mi >= 1) return `${why ?? 'Poor visibility'} — only about ${miles(mi)}`;
  return `${why ?? 'Very poor visibility'} — under 1 mile (about ${miles(mi)})`;
}

export interface WindPlain {
  mph: number;
  gustMph: number | null;
  /** "Wind from the west 10 mph, gusts 20 mph" / "Calm" / "Light, shifting wind 3 mph". */
  text: string;
  /** "W 10 mph" / "W 10 mph, gusts 20" / "calm". */
  short: string;
  gusty: boolean;
  windy: boolean;
}

/** Gusts at/above this (mph) count as "gusty" for the crew. */
export const GUSTY_MPH = 20;
/** Sustained wind at/above this (mph) counts as "windy". */
export const WINDY_MPH = 20;

export function windPlain(dir: number | 'VRB' | null, kt: number | null, gustKt: number | null): WindPlain | null {
  if (!ok(kt)) return null;
  const mph = Math.round(ktToMph(kt));
  const gustMph = ok(gustKt) && gustKt > 0 ? Math.round(ktToMph(gustKt)) : null;
  const gusty = gustMph !== null && gustMph >= GUSTY_MPH;
  const windy = mph >= WINDY_MPH;
  if (mph < 2 && gustMph === null) return { mph, gustMph, text: 'Calm', short: 'calm', gusty, windy };
  const g = gustMph !== null ? `, gusts ${gustMph} mph` : '';
  if (dir === 'VRB' || !ok(dir as number | null)) {
    const text = mph <= 6 ? `Light, shifting wind ${mph} mph${g}` : `Wind ${mph} mph, shifting direction${g}`;
    return { mph, gustMph, text, short: `${mph} mph${gustMph !== null ? `, gusts ${gustMph}` : ''}`, gusty, windy };
  }
  const d = dir as number;
  return {
    mph,
    gustMph,
    text: `Wind from the ${directionWord(d)} ${mph} mph${g}`,
    short: `${directionShort(d)} ${mph} mph${gustMph !== null ? `, gusts ${gustMph}` : ''}`,
    gusty,
    windy,
  };
}

/** "64°F". */
export function tempPlain(c: number | null): string | null {
  return ok(c) ? `${Math.round(cToF(c))}°F` : null;
}

/** Highest rain chance in the next `hours` hours of the hourly forecast. */
export function rainChance(g: GeneralWeather | null, t: EpochMs, hours = 3): { maxPct: number; text: string } | null {
  if (!g) return null;
  const next = g.hourly.filter((h) => h.t >= t - 30 * 60_000 && h.t <= t + hours * 3600_000 && ok(h.precipProbPct));
  if (!next.length) return ok(g.precipProbPct) ? { maxPct: Math.round(g.precipProbPct), text: rainLine(Math.round(g.precipProbPct), hours) } : null;
  const maxPct = Math.round(Math.max(...next.map((h) => h.precipProbPct!)));
  return { maxPct, text: rainLine(maxPct, hours) };
}

function rainLine(pct: number, hours: number): string {
  if (pct < 10) return `Rain unlikely next ${hours} h`;
  return `Rain chance next ${hours} h: up to ${pct}%`;
}

// ───────────────────────────── The whole picture ─────────────────────────────

export interface PlainWxInput {
  t: EpochMs;
  /** Latest METAR of the primary airport (or null). */
  metar: MetarReport | null;
  /** General (National Weather Service) weather for the mission area (or null). */
  general: GeneralWeather | null;
  /** Sun elevation (deg) at the reference, for night-aware sky words and light notes. */
  sunElevDeg?: number | null;
}

export interface PlainWx {
  source: 'metar' | 'general' | 'none';
  sky: Sky;
  /** "Low clouds at ~800 ft" — null when no cloud height is known / no clouds. */
  clouds: string | null;
  /** "Can see 10+ miles" / "Hazy — about 3 miles". */
  visibility: string | null;
  wind: WindPlain | null;
  /** "64°F". */
  temp: string | null;
  /** "Rain chance next 3 h: up to 40%" / "Rain unlikely next 3 h". */
  rain: string | null;
  /** Plain flight-category words (MVFR → "Low clouds / haze"); null for VFR / unknown. */
  category: string | null;
  /** Short light / sky notes for filming. */
  filming: string[];
  /** The one thing worth calling out on a small chip, or null when nothing is notable. */
  notable: { icon: string; word: string } | null;
  /** "☀️ Sunny · W 10 mph" / "☁️ Low clouds · gusty 20 mph". */
  summary: string;
}

/** Plain-language picture: METAR when there is one, else general weather. */
export function plainWeather(input: PlainWxInput): PlainWx {
  const { t, metar, general } = input;
  const elev = input.sunElevDeg ?? null;
  const isDay = elev === null ? true : elev > -0.833;
  const src: PlainWx['source'] = metar ? 'metar' : general ? 'general' : 'none';

  let kind: SkyKind = 'unknown';
  let clouds: string | null = null;
  let visibility: string | null = null;
  let wind: WindPlain | null = null;
  let tempC: number | null = null;
  let ceilingFt: number | null = null;
  let category: string | null = null;
  if (metar) {
    kind = skyFromMetar(metar);
    clouds = cloudText(metar);
    visibility = visibilityPlain(metar.visibilitySm, metar.wx);
    wind = windPlain(metar.windDirDeg, metar.windKt, metar.gustKt);
    tempC = metar.tempC;
    ceilingFt = metar.ceilingFt;
    category = categoryPlain(metar.flightCategory);
  }
  if (general) {
    if (kind === 'unknown') kind = skyFromWmo(general.weatherCode, general.cloudCoverPct);
    if (visibility === null && ok(general.visibilityM)) visibility = visibilityPlain(general.visibilityM * MI_PER_M, null);
    if (wind === null) wind = windPlain(general.windDirDeg, general.windKt, general.gustKt);
    if (tempC === null) tempC = general.tempC;
  }
  // a fair-weather METAR sky with model rain → trust the observed sky but keep the rain chance
  const sky = skyOf(kind, isDay);
  const rain = rainChance(general, t, 3);

  const lowCloud = ok(ceilingFt) && ceilingFt < 3000;
  const veryLow = (ok(ceilingFt) && ceilingFt < 1000) || kind === 'fog';
  let notable: PlainWx['notable'] = null;
  if (kind === 'thunder') notable = { icon: sky.icon, word: 'Storms' };
  else if (veryLow) notable = { icon: '🌫️', word: kind === 'fog' ? 'Fog' : 'Very low clouds' };
  else if (isWet(kind)) notable = { icon: sky.icon, word: kind === 'snow' ? 'Snow' : 'Rain' };
  else if (lowCloud) notable = { icon: '☁️', word: 'Low clouds' };
  else if (wind?.gusty || wind?.windy) notable = { icon: '💨', word: wind.gusty ? 'Gusty' : 'Windy' };
  else if (kind === 'haze') notable = { icon: sky.icon, word: 'Hazy' };
  else if (category && !lowCloud) notable = { icon: '🌫️', word: 'Hazy' };

  const filming = filmingNotes(kind, wind, elev, lowCloud || veryLow);

  // chip summary: sky (or the low-cloud word) · wind
  const skyWord = veryLow ? (kind === 'fog' ? 'Fog' : 'Very low clouds') : lowCloud ? 'Low clouds' : sky.text;
  const skyIcon = veryLow ? '🌫️' : lowCloud ? '☁️' : sky.icon;
  const windBit = wind ? (wind.gusty ? `gusty ${wind.gustMph} mph` : wind.short) : null;
  const summary = [`${skyIcon} ${skyWord}`, windBit].filter(Boolean).join(' · ');

  return { source: src, sky, clouds, visibility, wind, temp: tempPlain(tempC), rain: rain?.text ?? null, category, filming, notable, summary };
}

/** Short notes on light / sky for the camera team (never a go/no-go). */
export function filmingNotes(kind: SkyKind, wind: WindPlain | null, sunElevDeg: number | null, lowCloud: boolean): string[] {
  const out: string[] = [];
  const up = sunElevDeg === null || sunElevDeg > -0.833;
  if (!up) out.push('Sun is down — low light');
  if (kind === 'overcast' || kind === 'mostly_cloudy') out.push('Overcast — soft, even light, flat grey sky');
  else if (kind === 'partly_cloudy') out.push('Partly cloudy — light can change quickly as clouds pass');
  else if (kind === 'fog') out.push('Fog — the aircraft may fade from view at a distance');
  else if (kind === 'haze') out.push('Haze — lower contrast; distant aircraft look washed out');
  else if (isWet(kind)) out.push('Wet weather — protect gear, watch for drops on the lens');
  if (lowCloud && kind !== 'fog') out.push('Low clouds — the aircraft may go in and out of the cloud base');
  if (up && sunElevDeg !== null && (kind === 'sunny' || kind === 'mostly_sunny' || kind === 'partly_cloudy')) {
    if (sunElevDeg < 15) out.push('Low sun — warm light, long shadows, flare when shooting toward it');
    else if (sunElevDeg > 50) out.push('High sun — hard light, short dark shadows');
  }
  if (wind?.gusty || wind?.windy) out.push('Gusty — long lenses and tripods may shake');
  return out;
}

// ───────────────────────────── Hourly row ─────────────────────────────

export interface PlainHour {
  t: EpochMs;
  icon: string;
  sky: string;
  /** "64°" (°F) or "—". */
  temp: string;
  /** "W 10" mph (gusts as "10–20") or "calm" / "—". */
  wind: string;
  gusty: boolean;
  /** 0..100 or null. */
  rainPct: number | null;
}

export function plainHour(h: GeneralWeatherHour, isDay = true): PlainHour {
  const sky = skyOf(skyFromWmo(h.weatherCode, h.cloudCoverPct), isDay);
  const w = windPlain(h.windDirDeg, h.windKt, h.gustKt);
  let wind = '—';
  if (w) {
    if (w.short === 'calm') wind = 'calm';
    else {
      const d = ok(h.windDirDeg) ? `${directionShort(h.windDirDeg)} ` : '';
      wind = `${d}${w.mph}${w.gustMph !== null && w.gustMph > w.mph ? `–${w.gustMph}` : ''}`;
    }
  }
  return {
    t: h.t,
    icon: sky.icon,
    sky: sky.text,
    temp: ok(h.tempC) ? `${Math.round(cToF(h.tempC))}°` : '—',
    wind,
    gusty: !!w?.gusty,
    rainPct: ok(h.precipProbPct) ? Math.round(h.precipProbPct) : null,
  };
}

// ───────────────────────────── Sun in words ─────────────────────────────

/**
 * Where the sun is, in words: "Low in the west", "High in the south", "Below the horizon (west)",
 * plus where that is for someone facing `facingDeg` ("behind you", "on your left", "in front of you").
 */
export function sunPlain(azimuthDeg: number, elevationDeg: number, facingDeg: number | null = null): string {
  const dir = directionWord(azimuthDeg);
  let where: string;
  if (elevationDeg < -6) where = `Below the horizon (${dir})`;
  else if (elevationDeg < -0.833) where = `Just below the horizon (${dir})`;
  else if (elevationDeg < 15) where = `Low in the ${dir}`;
  else if (elevationDeg < 50) where = `In the ${dir}, mid-height`;
  else where = `High in the ${dir}`;
  if (facingDeg === null || !Number.isFinite(facingDeg) || elevationDeg < -0.833) return where;
  const d = angleDiffDeg(facingDeg, azimuthDeg);
  const rel = Math.abs(d) <= 45 ? 'in front of you' : Math.abs(d) >= 135 ? 'behind you' : d > 0 ? 'on your right' : 'on your left';
  return `${where}, ${rel}`;
}

// ───────────────────────────── Changes (alerts) ─────────────────────────────

const CAT_RANK: Record<FlightCategory, number> = { VFR: 0, MVFR: 1, IFR: 2, LIFR: 3, UNKNOWN: -1 };

/** A category transition alone does not establish which weather measurement changed. */
export function categoryChangePlain(prev: FlightCategory, cur: Pick<MetarReport, 'flightCategory' | 'ceilingFt' | 'visibilitySm' | 'wx'>): string {
  const before = CAT_RANK[prev], after = CAT_RANK[cur.flightCategory];
  const direction = before < 0 || after < 0 || before === after ? 'now' : after > before ? 'worsened to' : 'improved to';
  const ceiling = ok(cur.ceilingFt) && cur.ceilingFt >= 0 ? `; ceiling now ${roughFeet(cur.ceilingFt)}` : '';
  return `Report category ${direction} ${cur.flightCategory}${ceiling}`;
}
